import { isDemoBuild } from './siteConfig.js';

/** Flat public links (all preserved from original menu) */
export function getPublicNavItems() {
  return [
    { label: isDemoBuild ? 'Product overview' : 'Main Page', path: '/' },
    ...(isDemoBuild
      ? [{ label: 'AI management & pricing', path: '/for-communities' }]
      : [
          { label: 'Product tour', path: '/product' },
          { label: 'AI management & pricing', path: '/for-communities' },
        ]),
    { label: 'Neighborhood Map', path: '/neighborhood-map' },
    { label: 'Privacy Policy', path: '/privacy' },
    { label: 'Terms', path: '/terms' },
    ...(isDemoBuild ? [] : [{ label: 'Rentals', path: '/rentals' }]),
  ].sort((a, b) => a.label.localeCompare(b.label));
}

export const PUBLIC_NAV_GROUPS = [
  {
    id: 'explore',
    label: 'Explore',
    defaultOpen: true,
    getItems: () =>
      getPublicNavItems().filter((item) => item.path !== '/privacy' && item.path !== '/terms'),
  },
  {
    id: 'legal',
    label: 'Legal',
    defaultOpen: false,
    getItems: () =>
      getPublicNavItems().filter((item) => item.path === '/privacy' || item.path === '/terms'),
  },
];

/** Authenticated nav — every original link, grouped for scannability */
export const AUTH_NAV_GROUPS = [
  {
    id: 'essentials',
    label: 'Essentials',
    defaultOpen: true,
    items: [
      { label: 'Home', path: '/dashboard' },
      { label: 'Help', path: '/help' },
    ],
  },
  {
    id: 'hoa',
    label: 'HOA & billing',
    defaultOpen: false,
    items: [
      { label: 'My Invoices', path: '/invoices' },
      { label: 'Documents', path: '/hoa-docs' },
      { label: 'Board Members', path: '/board-members' },
      { label: 'Meeting Minutes', path: '/meeting-minutes' },
      { label: 'Governance Calendar', path: '/compliance-calendar' },
      { label: 'Shared Files', path: '/shared-files' },
      { label: 'Owner Contacts', path: '/owners' },
      { label: 'Contractors', path: '/contractors' },
      { label: 'Polls', path: '/polls' },
      { label: 'Violations', path: '/violations' },
      { label: 'ARC Requests', path: '/arc-review' },
      { label: 'Community Services', path: '/community-services' },
    ],
  },
  {
    id: 'access',
    label: 'Gate & mail',
    defaultOpen: false,
    items: [
      { label: 'Gate Access', path: '/gate-access' },
      { label: 'Mail Delivery', path: '/mailbox' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    defaultOpen: false,
    items: [
      { label: 'Message Board', path: '/message-board' },
      { label: 'Marketplace', path: '/marketplace' },
      { label: 'Calendar', path: '/calendar' },
      { label: 'Community Pics', path: '/community-pics' },
      { label: 'Rentals', path: '/rentals' },
    ],
  },
  {
    id: 'monitoring',
    label: 'Environment',
    defaultOpen: false,
    items: [
      { label: 'Well Monitoring', path: '/well-monitoring' },
      { label: 'Seismic Activity', path: '/seismic-monitoring' },
      { label: 'Road Conditions', path: '/road-conditions' },
      { label: 'Local River Info', path: '/local-river-info' },
      { label: 'Wildfire & Air Quality', path: '/environmental' },
    ],
  },
  {
    id: 'maps',
    label: 'Maps & sky',
    defaultOpen: false,
    items: [
      { label: 'Neighborhood Map', path: '/neighborhood-map' },
      { label: 'Flight Radar', path: '/flight-radar' },
      { label: 'Star Map', path: '/star-map' },
      { label: 'Onsite Webcam', path: '/video' },
    ],
  },
  {
    id: 'identify',
    label: 'Nature ID',
    defaultOpen: false,
    items: [
      { label: 'Plants & trees', path: '/plant-tree-identification', keywords: 'plant tree identification' },
      { label: 'Wildlife', path: '/animal-identification', keywords: 'animal wildlife identification' },
      { label: 'Rocks & minerals', path: '/rock-mineral-identification', keywords: 'rock mineral identification' },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    defaultOpen: false,
    items: [{ label: 'Terms', path: '/terms' }],
  },
];

export const ADMIN_NAV_ITEM = { label: 'Admin Portal', path: '/admin' };

export function flattenAuthNavItems() {
  return AUTH_NAV_GROUPS.flatMap((g) => g.items);
}

export const MAX_USER_QUICK_LINKS = 8;

export function resolveNavGroups(isPublic) {
  if (isPublic) {
    return PUBLIC_NAV_GROUPS.map((g) => ({
      id: g.id,
      label: g.label,
      defaultOpen: g.defaultOpen,
      items: g.getItems(),
    }));
  }
  return AUTH_NAV_GROUPS;
}

export function filterNavItems(groups, query) {
  const q = query.trim().toLowerCase();
  if (!q) return null;

  const hits = [];
  for (const group of groups) {
    for (const item of group.items) {
      const haystack = `${item.label} ${item.path} ${item.keywords || ''}`.toLowerCase();
      if (haystack.includes(q)) {
        hits.push({ ...item, groupLabel: group.label });
      }
    }
  }
  return hits;
}